import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Plus } from "lucide-react";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import {
  PurchaseInvoiceDetailLink,
  PurchaseOrderDetailLink,
  SupplierDetailLink,
} from "@/components/hub/HubEntityDetailLink";
import { QuickBooksBillTextLink, QuickBooksTxnLink } from "@/components/hub/QuickBooksTxnLink";
import { MegaventoryPurchaseInvoiceExternalLink } from "@/components/mrp/MegaventoryPurchaseOrderLinks";
import { Button } from "@/components/ui/Button";
import { FilterChip } from "@/components/ui/FilterChip";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { HoverHintContent, PageTitleWithHelp } from "@/components/ui/title-help";
import { buildInboundReceiptReviewPath } from "@/lib/hubEntityDetailPaths";
import { EM_DASH, cn } from "@/lib/utils";
import { Badge } from "@/components/ui/Badge";
import { type SortDirection } from "@/components/ui/SortableTableHeader";
import {
  confirmApInvoiceQboMatch,
  createApInvoiceDraft,
  fetchApInvoiceQboDocHint,
  fetchApInvoices,
  type ApInvoiceQboDocHint,
  type ApInvoiceRow,
  type ApInvoiceStatus,
  type ApInvoiceTier,
} from "./apInvoicesApi";
import { ApInvoiceCommentsGlyph } from "./ApInvoiceCommentsGlyph";
import { ApInvoiceCreateQboBillModal } from "./ApInvoiceCreateQboBillModal";
import {
  apInvoiceListCanConfirmQboMatch,
  apInvoiceListCanCreateQboBill,
  apInvoiceListConfirmQboMatchButtonLabel,
  apInvoiceListShowsStageDetail,
  apInvoiceListVendorInvoiceLabel,
  apInvoiceWorkflowBadgeTone,
} from "./apInvoiceStatusUi";
import {
  apInvoiceTshWorkflowBadgeTone,
  canCompleteApInvoiceTshWorkflow,
  formatApInvoiceLinkedTshMvNumber,
} from "./apInvoiceTshUi";
import {
  AP_INVOICE_LIST_DEFAULT_SORT_DIR,
  AP_INVOICE_LIST_DEFAULT_SORT_KEY,
  defaultSortDirectionForApInvoiceListColumn,
  sortApInvoiceListRows,
  type ApInvoiceListSortKey,
} from "./apInvoiceListSort";
import { useAccountingApInvoicesLiveQueries } from "@/lib/useAccountingApInvoicesLiveQueries";

const AP_INVOICES_LIST_KEY = ["accounting", "ap-invoices"] as const;

const STATUS_HELP_ROWS: Array<{ term: string; detail: string }> = [
  { term: "Draft", detail: "Not yet submitted." },
  { term: "AI reviewing", detail: "Extraction in progress." },
  { term: "Awaiting review", detail: "Verify fields, then submit." },
  { term: "No receipt", detail: "Submitted inventory invoice awaiting a matching dock receipt." },
  {
    term: "Discrepancy",
    detail: "The vendor invoice, receipt, or purchase order quantities do not agree. Resolve it before a purchase invoice can be created.",
  },
  { term: "GIPI not created", detail: "Receipt and vendor invoice agree, and the Megaventory purchase invoice has not been created yet." },
  {
    term: "Pending TSH",
    detail: "Shipping and handling still need a TSH document. This is the status while that work is open, even if the Megaventory purchase invoice already exists.",
  },
  {
    term: "GIPI in MV",
    detail: "Megaventory purchase invoice exists and TSH is no longer pending. QuickBooks verification happens from here.",
  },
  {
    term: "Non-PO expense",
    detail: "General ledger invoice that does not wait on a dock receipt. Use Create QuickBooks bill to post.",
  },
  {
    term: "QBO confirmed",
    detail:
      "Someone marked the QuickBooks bill as verified in the hub (read-only; the hub does not check QuickBooks automatically).",
  },
];

function VendorInvoicesPageHelpContent(): JSX.Element {
  return (
    <div className="w-[min(100vw-2rem,22rem)] space-y-3 text-left">
      <p>
        Enter vendor invoices and track approval, TSH workflow, and whether a Megaventory purchase invoice (GIPI) has
        been linked.
      </p>
      <p>
        This page does not create GIPI documents. Receipt capture happens in Shipping inbound. When the vendor invoice,
        receipt, and purchase order agree, the hub creates the purchase invoice automatically. A quantity that does not
        agree is a discrepancy on Receipt discrepancy until someone resolves it.
      </p>
      <p>
        Mark consulting, engineering, and similar bills as Non-PO expense so they skip receipt matching. Those stay ready
        to post until someone confirms the QuickBooks bill.
      </p>
      <div>
        <p className="mb-1.5 font-medium text-slate-800">Statuses</p>
        <ul className="list-none space-y-1.5 pl-0">
          {STATUS_HELP_ROWS.map((row) => (
            <li key={row.term}>
              <span className="font-medium text-slate-800">{row.term}</span>
              <span className="text-slate-600"> — {row.detail}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

type ApInvoiceListStatusFilterKey =
  | "draft"
  | "under_ai_review"
  | "awaiting_human_review"
  | "pending_tsh"
  | "discrepancy"
  | "tsh_needs_link_review"
  | "tsh_linked"
  | "non_po_purchase"
  | "1"
  | "2"
  | "3"
  | "4";

const STATUS_FILTER_OPTIONS: Array<{ key: ApInvoiceListStatusFilterKey; label: string }> = [
  { key: "draft", label: "Drafts" },
  { key: "under_ai_review", label: "AI reviewing" },
  { key: "awaiting_human_review", label: "Awaiting review" },
  { key: "1", label: "No receipt" },
  { key: "discrepancy", label: "Discrepancy" },
  { key: "2", label: "GIPI not created" },
  { key: "pending_tsh", label: "Pending TSH" },
  { key: "3", label: "GIPI in MV" },
  { key: "tsh_needs_link_review", label: "Needs TSH link review" },
  { key: "tsh_linked", label: "TSH linked" },
  { key: "non_po_purchase", label: "Non-PO Expense" },
  { key: "4", label: "QBO confirmed" },
];

function rowMatchesTshLinked(row: ApInvoiceRow): boolean {
  return row.tsh_purchase_invoice_id != null || row.tsh_workflow?.is_complete === true;
}

function rowMatchesStatusFilters(
  row: ApInvoiceRow,
  selected: ReadonlySet<ApInvoiceListStatusFilterKey>,
): boolean {
  if (selected.size === 0) {
    return true;
  }
  if (selected.has("non_po_purchase") && row.purchase_kind === "non_po_purchase") {
    return true;
  }
  if (selected.has("discrepancy") && row.stage === "receipt_discrepancy") {
    return true;
  }
  if (selected.has("tsh_needs_link_review") && row.tsh_workflow_status === "needs_link_review") {
    return true;
  }
  if (selected.has("tsh_linked") && rowMatchesTshLinked(row)) {
    return true;
  }
  if (row.status !== "submitted" && row.status !== "pending_tsh") {
    return selected.has(row.status);
  }
  if (row.stage === "receipt_discrepancy") {
    return selected.has("discrepancy");
  }
  if (row.status === "pending_tsh" || row.stage === "pending_tsh") {
    return selected.has("pending_tsh");
  }
  if (row.purchase_kind === "non_po_purchase") {
    return selected.has("4") && row.tier === 4;
  }
  return selected.has(String(row.tier) as ApInvoiceListStatusFilterKey);
}

function tierBadgeClass(tier: ApInvoiceTier): string {
  switch (tier) {
    case 0:
      return "bg-violet-50 text-violet-900 ring-violet-200";
    case 1:
      return "bg-slate-100 text-slate-700 ring-slate-200";
    case 2:
      return "bg-amber-50 text-amber-900 ring-amber-200";
    case 3:
      return "bg-sky-50 text-sky-900 ring-sky-200";
    case 4:
      return "bg-emerald-50 text-emerald-900 ring-emerald-200";
    default:
      return "bg-slate-100 text-slate-700 ring-slate-200";
  }
}

function formatMoney(amount: string | null, currency: string): string {
  if (amount == null || amount.trim() === "") return "—";
  const parsed = Number.parseFloat(amount);
  if (!Number.isFinite(parsed)) return amount;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.trim() !== "" ? currency : "USD",
      maximumFractionDigits: 2,
    }).format(parsed);
  } catch {
    return `${parsed.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${currency}`;
  }
}

function formatDate(raw: string | null): string {
  if (raw == null || raw.trim() === "") return "—";
  const parsed = new Date(raw.includes("T") ? raw : `${raw}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString();
}

function apInvoiceVerifierInitials(displayName: string | null): string {
  const parts = (displayName ?? "").trim().split(/\s+/).filter((part) => part !== "");
  return parts.map((part) => part.charAt(0).toUpperCase()).join("");
}

function apInvoiceQboVerifiedLabel(displayName: string | null, confirmedAt: string | null): string {
  const initials = apInvoiceVerifierInitials(displayName);
  const parsed = confirmedAt == null || confirmedAt.trim() === "" ? null : new Date(confirmedAt);
  const date =
    parsed == null || Number.isNaN(parsed.getTime())
      ? (confirmedAt ?? "").trim()
      : parsed.toLocaleDateString();
  if (initials === "") {
    return date === "" ? "Verified" : `Verified ${date}`;
  }
  return date === "" ? `${initials} verified` : `${initials} verified ${date}`;
}

function apInvoiceQboPaidLabel(paid: boolean | null): string {
  if (paid === true) return "Paid";
  if (paid === false) return "Not paid";
  return "Payment status unavailable";
}

function ApInvoiceQboDocHoverBody({ invoiceId }: { invoiceId: string }) {
  const hintQuery = useQuery({
    queryKey: ["ap-invoice-qbo-doc-hint", invoiceId],
    queryFn: () => fetchApInvoiceQboDocHint(invoiceId),
    staleTime: 60_000,
  });

  if (hintQuery.isPending) {
    return <p>Loading QuickBooks bill.</p>;
  }
  if (hintQuery.isError) {
    const message = hintQuery.error instanceof Error ? hintQuery.error.message : "Could not load QuickBooks bill.";
    return <p>{message}</p>;
  }

  return <ApInvoiceQboDocHoverDetails hint={hintQuery.data} />;
}

function ApInvoiceQboDocHoverDetails({ hint }: { hint: ApInvoiceQboDocHint }) {
  const verifiedBy = hint.verified_by_name?.trim() || "unknown";
  return (
    <div className="space-y-1">
      <p>Verified by {verifiedBy}</p>
      {hint.bills.length === 0 ? (
        <p>QuickBooks id unavailable</p>
      ) : (
        hint.bills.map((bill) => (
          <div key={`${bill.label}-${bill.qbo_id ?? "none"}`}>
            <p>
              {hint.bills.length > 1 ? `${bill.label} ` : ""}
              QuickBooks id {bill.qbo_id ?? "unavailable"}
            </p>
            <p>{apInvoiceQboPaidLabel(bill.paid)}</p>
          </div>
        ))
      )}
      {hint.error != null && hint.error.trim() !== "" ? <p>{hint.error}</p> : null}
    </div>
  );
}

function parseApInvoicePositiveInt(raw: string | null | undefined): number | null {
  if (raw == null || raw.trim() === "") {
    return null;
  }
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function ApInvoiceReceiptsCell({ row }: { row: ApInvoiceRow }) {
  if (row.purchase_kind === "non_po_purchase") {
    return <span className="text-slate-400">—</span>;
  }

  if (row.inbound_receipt_id != null) {
    return (
      <Link to={buildInboundReceiptReviewPath(row.inbound_receipt_id)} className="text-brand-700 hover:underline">
        {row.inbound_receipt_id}
      </Link>
    );
  }

  return <span className="text-slate-400">{EM_DASH}</span>;
}

function ApInvoiceVendorCell({ row }: { row: ApInvoiceRow }) {
  const supplierId = parseApInvoicePositiveInt(row.supplier_id);
  if (supplierId != null && row.supplier_name != null && row.supplier_name.trim() !== "") {
    return (
      <SupplierDetailLink supplierId={supplierId} supplierName={row.supplier_name} className="font-normal" />
    );
  }
  if (row.supplier_name != null && row.supplier_name.trim() !== "") {
    return <span className="text-slate-700">{row.supplier_name}</span>;
  }
  return <span className="text-slate-400">—</span>;
}

function ApInvoiceGipiCell({ row }: { row: ApInvoiceRow }) {
  if (row.purchase_invoice_mv_number == null || row.purchase_invoice_mv_number.trim() === "") {
    return null;
  }

  const purchaseInvoiceId = parseApInvoicePositiveInt(row.purchase_invoice_id);
  const purchaseInvoiceMvId = parseApInvoicePositiveInt(row.purchase_invoice_mv_id);

  return (
    <div className="space-y-1">
      <span className="inline-flex flex-nowrap items-center gap-1 whitespace-nowrap">
        {purchaseInvoiceId != null ? (
          <PurchaseInvoiceDetailLink
            purchaseInvoiceId={purchaseInvoiceId}
            mvNumber={row.purchase_invoice_mv_number}
            vendorInvoiceNumber={row.vendor_invoice_number}
          />
        ) : (
          <span className="text-slate-900">{row.purchase_invoice_mv_number}</span>
        )}
        {purchaseInvoiceMvId != null ? (
          <MegaventoryPurchaseInvoiceExternalLink
            purchaseInvoiceMvId={purchaseInvoiceMvId}
            mvNumber={row.purchase_invoice_mv_number}
          />
        ) : null}
        {(row.purchase_invoice_qb_id?.trim() ?? "") !== "" ? (
          <QuickBooksBillTextLink
            txnId={row.purchase_invoice_qb_id}
            documentLabel={row.purchase_invoice_mv_number ?? "purchase invoice"}
          />
        ) : null}
      </span>
    </div>
  );
}

function ApInvoiceQuickBooksCell({ row, onUpdated }: { row: ApInvoiceRow; onUpdated: () => void }) {
  const [qboBillModalOpen, setQboBillModalOpen] = useState(false);

  const confirmMutation = useMutation({
    mutationFn: () => confirmApInvoiceQboMatch(row.id),
    onSuccess: () => onUpdated(),
  });

  const canCreateQboBill = apInvoiceListCanCreateQboBill(row);
  const canConfirmQbo = apInvoiceListCanConfirmQboMatch(row);

  const gipiBillId = row.qbo_gipi_bill_id?.trim() || row.purchase_invoice_qb_id?.trim() || "";
  const tshBillId = row.qbo_tsh_bill_id?.trim() || row.tsh_purchase_invoice_qb_id?.trim() || "";
  const hubBillId = row.qbo_bill_id?.trim() ?? "";
  const hasGipiBill = gipiBillId !== "";
  const hasTshBill = tshBillId !== "";
  const hasHubBill = hubBillId !== "";
  const hasBillLinks = hasGipiBill || hasTshBill || hasHubBill;
  const hasConfirmed = row.qbo_match_confirmed_at != null;
  const hasFailedSync = row.qbo_bill_sync_status === "failed" && row.qbo_bill_sync_error?.trim();
  const hasActions = canCreateQboBill || canConfirmQbo;

  if (!hasBillLinks && !hasConfirmed && !hasFailedSync && !hasActions) {
    return <span className="text-slate-400">—</span>;
  }

  return (
    <div className="space-y-2 text-sm">
      {hasGipiBill ? (
        <div className="text-xs text-slate-600">
          GIPI QBO ID{" "}
          <QuickBooksTxnLink txnId={gipiBillId} kind="bill" documentLabel="Megaventory GIPI QuickBooks bill" />
        </div>
      ) : null}
      {hasTshBill ? (
        <div className="text-xs text-slate-600">
          TSH QBO ID{" "}
          <QuickBooksTxnLink txnId={tshBillId} kind="bill" documentLabel="Megaventory TSH QuickBooks bill" />
        </div>
      ) : null}
      {hasHubBill ? (
        <div className="text-xs text-slate-600">
          QBO ID{" "}
          <QuickBooksTxnLink txnId={hubBillId} kind="bill" documentLabel="QuickBooks bill" />
        </div>
      ) : null}
      {hasConfirmed ? (
        <HoverHintContent hoverHint={<ApInvoiceQboDocHoverBody invoiceId={row.id} />}>
          <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {apInvoiceQboVerifiedLabel(row.qbo_match_confirmed_by_name, row.qbo_match_confirmed_at)}
          </span>
        </HoverHintContent>
      ) : null}
      {hasFailedSync ? (
        <p className="max-w-[14rem] text-xs text-rose-700">{row.qbo_bill_sync_error?.trim()}</p>
      ) : null}
      {canCreateQboBill ? (
        <button
          type="button"
          className="inline-block h-auto min-h-8 max-w-[11rem] whitespace-normal rounded-md border border-slate-300 bg-white px-3 py-1.5 text-center text-sm font-medium leading-snug text-slate-900 hover:bg-slate-100"
          onClick={() => setQboBillModalOpen(true)}
        >
          Create QuickBooks bill
        </button>
      ) : canConfirmQbo ? (
        <button
          type="button"
          className="inline-block h-auto min-h-8 max-w-[11rem] whitespace-normal rounded-md border border-slate-300 bg-white px-3 py-1.5 text-center text-sm font-medium leading-snug text-slate-900 hover:bg-slate-100 disabled:cursor-not-allowed disabled:bg-slate-50"
          disabled={confirmMutation.isPending}
          onClick={() => confirmMutation.mutate()}
        >
          {apInvoiceListConfirmQboMatchButtonLabel(confirmMutation.isPending)}
        </button>
      ) : null}
      {confirmMutation.isError ? (
        <p className="text-xs text-rose-700">
          {confirmMutation.error instanceof Error ? confirmMutation.error.message : String(confirmMutation.error)}
        </p>
      ) : null}
      {qboBillModalOpen ? (
        <ApInvoiceCreateQboBillModal
          invoice={row}
          onClose={() => setQboBillModalOpen(false)}
          onCreated={() => {
            setQboBillModalOpen(false);
            onUpdated();
          }}
        />
      ) : null}
    </div>
  );
}

export function AccountsPayablePage() {
  const queryClient = useQueryClient();
  useAccountingApInvoicesLiveQueries();
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilterSelected, setStatusFilterSelected] = useState<Set<ApInvoiceListStatusFilterKey>>(
    () => new Set(),
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setSearch(searchDraft), 300);
    return () => window.clearTimeout(timeoutId);
  }, [searchDraft]);

  const statusFilterKey = useMemo(
    () => Array.from(statusFilterSelected).sort().join("|"),
    [statusFilterSelected],
  );

  const listQuery = useQuery({
    queryKey: [...AP_INVOICES_LIST_KEY, search, statusFilterKey],
    queryFn: () => fetchApInvoices(search.trim() !== "" ? search : null, null, null),
    staleTime: 15_000,
  });

  const filteredRows = useMemo(() => {
    const rows = listQuery.data ?? [];
    return rows.filter((row) => rowMatchesStatusFilters(row, statusFilterSelected));
  }, [listQuery.data, statusFilterSelected]);

  const [sortKey, setSortKey] = useState<ApInvoiceListSortKey>(AP_INVOICE_LIST_DEFAULT_SORT_KEY);
  const [sortDirection, setSortDirection] = useState<SortDirection>(AP_INVOICE_LIST_DEFAULT_SORT_DIR);

  const sortedRows = useMemo(
    () => sortApInvoiceListRows(filteredRows, sortKey, sortDirection),
    [filteredRows, sortKey, sortDirection],
  );

  const onSort = useCallback((nextKey: string): void => {
    const key = nextKey as ApInvoiceListSortKey;
    if (key === sortKey) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection(defaultSortDirectionForApInvoiceListColumn(key));
  }, [sortKey]);

  const toggleStatusFilter = useCallback((key: ApInvoiceListStatusFilterKey): void => {
    setStatusFilterSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const invalidateList = () => queryClient.invalidateQueries({ queryKey: AP_INVOICES_LIST_KEY });

  const columns = useMemo<HubDataTableColumn<ApInvoiceRow, ApInvoiceListSortKey>[]>(
    () => [
      {
        id: "vendor_invoice",
        header: "Vendor invoice",
        sortKey: "vendor_invoice",
        cell: (row) => <ApInvoiceListInvoiceCell row={row} />,
      },
      {
        id: "invoice_date",
        header: "Invoice date",
        sortKey: "invoice_date",
        overflow: "nowrap",
        cell: (row) => formatDate(row.invoice_date),
      },
      {
        id: "vendor",
        header: "Vendor",
        sortKey: "vendor",
        cell: (row) => <ApInvoiceVendorCell row={row} />,
      },
      {
        id: "purchase_order",
        header: "Purchase order",
        sortKey: "purchase_order",
        overflow: "nowrap",
        cell: (row) => <ApInvoicePurchaseOrderCell row={row} />,
      },
      {
        id: "amount",
        header: "Amount",
        sortKey: "amount",
        align: "right",
        overflow: "nowrap",
        cell: (row) => formatMoney(row.total_amount, row.currency),
      },
      {
        id: "receipts",
        header: "Receipt",
        sortKey: "receipts",
        cell: (row) => <ApInvoiceReceiptsCell row={row} />,
      },
      {
        id: "megaventory",
        header: "Megaventory",
        sortKey: "megaventory",
        overflow: "nowrap",
        cell: (row) => <ApInvoiceMegaventoryCell row={row} />,
      },
      {
        id: "quickbooks",
        header: "QuickBooks",
        sortKey: "quickbooks",
        cell: (row) => <ApInvoiceQuickBooksCell row={row} onUpdated={invalidateList} />,
      },
    ],
    [invalidateList],
  );

  const invoiceCountTitle =
    listQuery.isLoading
      ? "Loading..."
      : statusFilterSelected.size > 0 && filteredRows.length !== (listQuery.data?.length ?? 0)
        ? `${sortedRows.length} of ${listQuery.data?.length ?? 0} invoices`
        : `${sortedRows.length} invoices`;
  const navigate = useNavigate();
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [createDraftError, setCreateDraftError] = useState<string | null>(null);

  const handleAddInvoice = useCallback(async () => {
    setCreateDraftError(null);
    setCreatingDraft(true);
    try {
      const invoice = await createApInvoiceDraft();
      queryClient.setQueryData(["accounting", "ap-invoice", invoice.id], invoice);
      navigate(`/accounting/ap/${encodeURIComponent(invoice.id)}/edit`);
    } catch (error) {
      setCreateDraftError(error instanceof Error ? error.message : String(error));
    } finally {
      setCreatingDraft(false);
    }
  }, [navigate, queryClient]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp
          hoverHintContent={<VendorInvoicesPageHelpContent />}
          hoverHintAriaLabel="About vendor invoices"
        >
          Vendor invoices
        </PageTitleWithHelp>
        <Button
          type="button"
          size="sm"
          isLoading={creatingDraft}
          onClick={() => void handleAddInvoice()}
        >
          <Plus className="h-4 w-4" aria-hidden />
          Add invoice
        </Button>
      </div>

      {createDraftError != null ? (
        <p className="text-sm text-rose-700">{createDraftError}</p>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <div>
            <Label htmlFor="ap-invoice-search">Search invoices</Label>
            <Input
              id="ap-invoice-search"
              type="search"
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              placeholder="Vendor invoice number, PO, vendor name, GIPI, TSH…"
              className="mt-1.5"
            />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="shrink-0 text-xs font-medium uppercase tracking-wide text-slate-500">Status</span>
              {STATUS_FILTER_OPTIONS.map((option) => {
                const isOn = statusFilterSelected.has(option.key);
                return (
                  <FilterChip
                    key={option.key}
                    pressed={isOn}
                    onClick={() => toggleStatusFilter(option.key)}
                  >
                    {option.label}
                  </FilterChip>
                );
              })}
            </div>
          </div>
        </CardBody>
      </Card>

      <HubDataTable
        columns={columns}
        rows={sortedRows}
        rowKey={(row) => row.id}
        title={invoiceCountTitle}
        sortKey={sortKey}
        sortDir={sortDirection}
        onSort={onSort}
        loading={listQuery.isLoading}
        loadingMessage="Loading invoices..."
        error={
          listQuery.isError
            ? listQuery.error instanceof Error
              ? listQuery.error.message
              : String(listQuery.error)
            : undefined
        }
        empty="No invoices match these filters."
      />
    </div>
  );
}

function ApInvoiceWorkflowStageBadge({
  status,
  statusLabel,
}: {
  status: ApInvoiceStatus;
  statusLabel: string;
}) {
  return <Badge tone={apInvoiceWorkflowBadgeTone(status)}>{statusLabel}</Badge>;
}

function ApInvoiceListStageUnderTitle({ row }: { row: ApInvoiceRow }) {
  const showStageDetail = apInvoiceListShowsStageDetail(row);
  const showDocumentStage =
    row.status === "submitted" ||
    row.stage === "receipt_discrepancy";
  const discrepancy = row.stage === "receipt_discrepancy";

  const documentBadge = (
    <span
      className={cn(
        "inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1",
        discrepancy ? "bg-red-50 text-red-900 ring-red-200" : tierBadgeClass(row.tier),
      )}
    >
      {row.tier_label}
    </span>
  );
  const stageDetail = showStageDetail ? (
    <span className="text-xs text-slate-500">{row.stage_label}</span>
  ) : null;
  const qboConfirmedHint = row.stage === "qbo_confirmed";

  return (
    <div className="flex flex-col items-start gap-1">
      {showDocumentStage && qboConfirmedHint ? (
        <HoverHintContent
          className="inline-flex flex-col items-start gap-1"
          hoverHint={<ApInvoiceQboDocHoverBody invoiceId={row.id} />}
        >
          {documentBadge}
        </HoverHintContent>
      ) : showDocumentStage ? (
        documentBadge
      ) : (
        <ApInvoiceWorkflowStageBadge status={row.status} statusLabel={row.status_label} />
      )}
      {qboConfirmedHint ? null : stageDetail}
      {row.gipi_sync_status === "waiting_on_holds" ? (
        <span className="text-xs text-amber-700">
          {row.gipi_sync_error != null && row.gipi_sync_error.trim() !== ""
            ? row.gipi_sync_error
            : "Waiting on holds before the GIPI."}
        </span>
      ) : row.gipi_sync_status === "failed" || row.gipi_sync_status === "creating" ? (
        <span className="text-xs text-red-700">
          {row.gipi_sync_error != null && row.gipi_sync_error.trim() !== ""
            ? row.gipi_sync_error
            : row.gipi_sync_status === "creating"
              ? "GIPI creation has not finished."
              : "GIPI creation failed."}
        </span>
      ) : row.gipi_sync_error != null && row.gipi_sync_error.trim() !== "" ? (
        <span className="text-xs text-red-700">{row.gipi_sync_error}</span>
      ) : null}
      {row.receipt_match_status === "mismatch" && !discrepancy ? (
        <Badge tone="danger">{row.receipt_match_status_label ?? "Receipt discrepancy"}</Badge>
      ) : null}
    </div>
  );
}

function ApInvoiceListInvoiceCell({ row }: { row: ApInvoiceRow }) {
  const vendorInvoiceListLabel = apInvoiceListVendorInvoiceLabel(row);
  const editPath = `/accounting/ap/${encodeURIComponent(row.id)}/edit`;

  return (
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={editPath} className="font-medium text-brand-700 hover:underline">
            {vendorInvoiceListLabel}
          </Link>
          {row.purchase_kind === "non_po_purchase" || row.purchase_kind === "standalone_tsh" ? (
            <Badge tone="info">{row.purchase_kind_label}</Badge>
          ) : null}
        </div>
        <ApInvoiceListStageUnderTitle row={row} />
      </div>
      <ApInvoiceCommentsGlyph
        invoiceId={row.id}
        commentCount={row.comment_count}
        preview={row.comment_preview}
      />
    </div>
  );
}

function ApInvoiceTshCell({ row }: { row: ApInvoiceRow }) {
  const linkedTshLabel = formatApInvoiceLinkedTshMvNumber(row.tsh_purchase_invoice_mv_number);
  const tshPurchaseInvoiceId = parseApInvoicePositiveInt(row.tsh_purchase_invoice_id);
  const gipiMissing = row.purchase_invoice_id == null || row.purchase_invoice_id.trim() === "";
  const waitingForGipi = (row.tsh_workflow_status ?? "").trim().toLowerCase() === "waiting_for_gipi";
  const hasWorkflowBadge =
    row.status === "pending_tsh" &&
    row.tsh_workflow_status_label != null &&
    row.tsh_workflow_status_label.trim() !== "" &&
    !(waitingForGipi && gipiMissing);
  const showCompleteTshWorkflow = row.status === "pending_tsh" && canCompleteApInvoiceTshWorkflow(row);

  if (linkedTshLabel === "" && !hasWorkflowBadge && !showCompleteTshWorkflow) {
    return null;
  }

  return (
    <div className="space-y-1 text-sm">
      {hasWorkflowBadge ? (
        <Badge tone={apInvoiceTshWorkflowBadgeTone(row.tsh_workflow_status)}>
          {row.tsh_workflow_status_label}
        </Badge>
      ) : null}
      {linkedTshLabel !== "" ? (
        <div className="inline-flex flex-wrap items-center gap-1">
          {tshPurchaseInvoiceId != null ? (
            <PurchaseInvoiceDetailLink
              purchaseInvoiceId={tshPurchaseInvoiceId}
              mvNumber={linkedTshLabel}
              vendorInvoiceNumber={row.vendor_invoice_number}
              className="text-sm font-normal"
            />
          ) : (
            <span className="text-emerald-800">{linkedTshLabel}</span>
          )}
          {(() => {
            const tshMegaventoryId = parseApInvoicePositiveInt(row.tsh_purchase_invoice_mv_id);
            if (tshMegaventoryId == null) return null;
            return (
              <MegaventoryPurchaseInvoiceExternalLink
                purchaseInvoiceMvId={tshMegaventoryId}
                mvNumber={linkedTshLabel}
              />
            );
          })()}
          <QuickBooksBillTextLink
            txnId={row.tsh_purchase_invoice_qb_id}
            documentLabel={linkedTshLabel}
          />
        </div>
      ) : null}
      {showCompleteTshWorkflow ? (
        <Link
          to={`/accounting/ap/${encodeURIComponent(row.id)}/edit`}
          className="inline-flex min-h-8 items-center whitespace-nowrap rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium leading-tight text-slate-900 hover:bg-slate-50"
        >
          Complete TSH workflow
        </Link>
      ) : null}
    </div>
  );
}

function ApInvoicePurchaseOrderCell({ row }: { row: ApInvoiceRow }) {
  if (row.purchase_order_id == null) {
    return <>—</>;
  }
  const purchaseOrderId = parseApInvoicePositiveInt(row.purchase_order_id);
  if (purchaseOrderId != null) {
    return (
      <PurchaseOrderDetailLink
        purchaseOrderId={purchaseOrderId}
        purchaseOrderNumber={row.purchase_order_number}
        className="font-normal"
      />
    );
  }
  return <span className="text-slate-700">{row.purchase_order_number ?? row.purchase_order_id}</span>;
}

function ApInvoiceMegaventoryCell({ row }: { row: ApInvoiceRow }) {
  const hasGipi = (row.purchase_invoice_mv_number ?? "").trim() !== "";
  const hasTshDocument = formatApInvoiceLinkedTshMvNumber(row.tsh_purchase_invoice_mv_number) !== "";
  const waitingForGipi = (row.tsh_workflow_status ?? "").trim().toLowerCase() === "waiting_for_gipi";
  const gipiMissing = row.purchase_invoice_id == null || row.purchase_invoice_id.trim() === "";
  const hasWorkflowBadge =
    row.status === "pending_tsh" &&
    (row.tsh_workflow_status_label ?? "").trim() !== "" &&
    !(waitingForGipi && gipiMissing);
  const showCompleteTshWorkflow = row.status === "pending_tsh" && canCompleteApInvoiceTshWorkflow(row);
  if (!hasGipi && !hasTshDocument && !hasWorkflowBadge && !showCompleteTshWorkflow) {
    return <span className="text-slate-400">{EM_DASH}</span>;
  }
  return (
    <div className="space-y-1">
      {hasGipi ? <ApInvoiceGipiCell row={row} /> : null}
      {hasTshDocument || hasWorkflowBadge || showCompleteTshWorkflow ? <ApInvoiceTshCell row={row} /> : null}
    </div>
  );
}
